import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "@phosphor-icons/react";

const CONTACT = "hello@dftbdigital.com";

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="space-y-6 max-w-2xl">
      <div>
        <Link
          to="/info"
          data-testid="privacy-back"
          className="inline-flex items-center gap-1 text-xs font-mono-time uppercase tracking-widest text-[#7A828C] hover:text-[#000000]"
        >
          <ArrowLeft size={12} /> Back to info
        </Link>
        <div className="mt-2 flex items-center gap-2">
          <ShieldCheck size={20} className="text-[#000000]" weight="duotone" />
          <div className="font-mono-time text-xs uppercase text-[#7A828C] tracking-widest">Legal</div>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-1">Privacy Policy</h1>
        <p className="text-xs text-[#7A828C] mt-1">Last updated: 19 May 2026</p>
      </div>

      <section className="text-sm text-[#5a5a5a] leading-relaxed space-y-3">
        <p>
          This Privacy Policy explains how <strong>Don&apos;t Forget The Bubbles Ltd</strong> (&quot;DFTB&quot;, &quot;we&quot;, &quot;us&quot;)
          collects, uses and protects personal information you provide when you use the DFTB26 conference application
          (the &quot;App&quot;), available as a Progressive Web App and as a native iOS/Android app.
        </p>
        <p>
          We are committed to handling your data in accordance with the UK General Data Protection Regulation (UK GDPR)
          and the Data Protection Act 2018. DFTB is the data controller.
        </p>
      </section>

      <Block title="1. What we collect">
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Account data:</strong> email address, name, and (optionally) affiliation, role and short bio.</li>
          <li><strong>Authentication:</strong> a hashed password and an HttpOnly session cookie.</li>
          <li><strong>In-app activity:</strong> questions you ask during sessions, poll votes, your networking profile,
            and 1:1 messages sent to other delegates.</li>
          <li><strong>Push subscriptions:</strong> if you grant permission, the App stores a Web Push subscription endpoint
            so we can deliver announcements (e.g. room changes).</li>
          <li><strong>Technical data:</strong> minimal request logs (timestamp, route, response code) used only to operate
            the service. We do not run third-party analytics.</li>
        </ul>
      </Block>

      <Block title="2. How we use your data">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>To run the conference experience: programme, Q&amp;A, polls, announcements.</li>
          <li>To enable networking and 1:1 messaging - only if you opt in.</li>
          <li>To send time-sensitive operational notifications (e.g. session room changes).</li>
          <li>To investigate abuse, technical errors and security incidents.</li>
        </ul>
      </Block>

      <Block title="3. Legal basis (UK GDPR)">
        <p>
          We rely on <strong>contract</strong> (to deliver the App you signed up for), <strong>consent</strong>
          {" "}(push notifications) and <strong>legitimate interests</strong> (running the in-app networking
          directory between authenticated delegates, security and abuse prevention). You can opt out of the
          networking directory at any time from Profile.
        </p>
      </Block>

      <Block title="4. Sharing">
        <p>
          We do not sell or rent your data. Your networking profile (name, affiliation, bio) is visible to other
          authenticated delegates by default. You can hide it at any time by switching off the &quot;Allow other
          delegates to message me&quot; toggle in Profile (or the &quot;Visible&quot; toggle on the Network tab). Messages
          are visible only to you and the recipient. We use the following processors:
        </p>
        <ul className="list-disc pl-5 space-y-1.5 mt-2">
          <li><strong>MongoDB Atlas</strong> - database hosting (EU region).</li>
          <li><strong>Emergent</strong> - application hosting and the LLM gateway used by admins to sync the schedule
            from the public dftb26.com website.</li>
          <li><strong>Apple Push / Google FCM / Web Push</strong> - only the push endpoint, used to deliver notifications.</li>
        </ul>
      </Block>

      <Block title="5. Retention">
        <p>
          Account, Q&amp;A and message data are kept for the duration of DFTB26 and for up to 90 days after the conference
          for support and audit purposes, after which they are deleted or anonymised. You can request earlier deletion at
          any time (see §8).
        </p>
      </Block>

      <Block title="6. Security">
        <p>
          Passwords are hashed with bcrypt. Sessions use HttpOnly, Secure, SameSite cookies. All traffic is encrypted in
          transit (HTTPS). Admin actions are recorded in an audit log.
        </p>
      </Block>

      <Block title="7. Children">
        <p>
          The App is intended for healthcare professionals and is not directed at children under 16. We do not knowingly
          collect data from children.
        </p>
      </Block>

      <Block title="8. Your rights">
        <p>
          Under UK GDPR you have the right to access, correct, delete, restrict, port or object to processing of your
          personal data, and to withdraw consent at any time. Email us at{" "}
          <a href={`mailto:${CONTACT}`} className="text-[#000000] hover:underline">{CONTACT}</a>{" "}
          and we will respond within 30 days. You may also complain to the UK Information Commissioner&apos;s Office (ICO)
          at <a href="https://ico.org.uk" target="_blank" rel="noreferrer" className="text-[#000000] hover:underline">ico.org.uk</a>.
        </p>
      </Block>

      <Block title="9. Cookies">
        <p>
          The App uses a single first-party HttpOnly session cookie required to keep you signed in. No advertising or
          analytics cookies are used.
        </p>
      </Block>

      <Block title="10. Changes">
        <p>
          We may update this policy ahead of and during DFTB26. Material changes will be highlighted in the App via an
          announcement.
        </p>
      </Block>

      <Block title="11. Contact">
        <p>
          Don&apos;t Forget The Bubbles Ltd -{" "}
          <a href={`mailto:${CONTACT}`} className="text-[#000000] hover:underline">{CONTACT}</a>
        </p>
      </Block>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
      <h2 className="font-display text-lg mb-2">{title}</h2>
      <div className="text-sm text-[#5a5a5a] leading-relaxed">{children}</div>
    </section>
  );
}
